import { COMMON_TEXT } from '@/constants';
import { ICertification, IUser } from '@/types';
import { TextContent } from '@/components/Text';
import { useCurrentUserFeature } from '@/features/users/currentUser/userCurrentUserFeature';
import React from 'react';
import UserInfo from '../UserInfo';

interface UserProfileProps {
  isLoading?: boolean;
  user: IUser | null;
  userCertifications: ICertification[];
}

/**
 * Component for displaying a user's profile information.
 * This component is intended to be used within the /profile route and will display
 * user-specific details such as name, role, certifications, and other relevant information.
 */
export const UserProfile: React.FC<UserProfileProps> = ({
  isLoading,
  user,
  userCertifications = [],
}) => {
  const { currentUserFullName, currentUserRole, currentUserImageUrl } = useCurrentUserFeature({
    currentUser: user,
  });

  // ===============================
  // Render
  // ===============================
  return (
    <div className="w-full">
      <div className="px-0 pb-6">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-12 lg:gap-6">
          <div className="col-span-12 space-y-2 md:col-span-7 lg:col-span-9">
            <TextContent
              isLoading={isLoading}
              label={COMMON_TEXT.USER_PROFILE}
              level={1}
              type="headline"
              variant="pageTitle"
            />
            <TextContent
              className="max-w-xl"
              color="neutral-gray-700"
              isLoading={isLoading}
              label={
                'This is your profile page where you can view your information and certifications.'
              }
              type="paragraph"
              variant="bodyMedium"
            />
          </div>
          <div className="col-span-12 md:col-span-3">
            <div className="ml-auto flex translate-y-1 items-center space-x-4">
              <UserInfo
                contentAlignment="right"
                imageSize={56}
                imageUrl={currentUserImageUrl}
                isLoading={isLoading}
                name={currentUserFullName}
                textAlignment="right"
                textSide="left"
                title={currentUserRole}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

UserProfile.displayName = 'UserProfile';

export default UserProfile;
